'use client'

import { useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

const formSchema = z.object({
  email: z.email({
    error: 'Please enter a valid email address.'
  })
})

/**
 * The "Stay in the loop" newsletter sign-up, sized for the marketing footer so it appears on every
 * page. Posts to the Customer.io `sj-prelaunch-opt-in` form, as the homepage section it replaced did.
 */
export function NewsletterSignup() {
  const [submitted, setSubmitted] = useState(false)
  const [failed, setFailed] = useState(false)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { email: '' }
  })

  async function onSubmit(values: z.infer<typeof formSchema>) {
    const formData = new URLSearchParams({ email: values.email })
    const cio_site_id = process.env.NEXT_PUBLIC_CUSTOMER_IO_SITE_ID
    const endpoint = `https://customerioforms.com/forms/submit_action?site_id=${cio_site_id}&form_id=sj-prelaunch-opt-in&success_url=https://schooljourney.com/submitted`

    // `no-cors`: the response is opaque, but the promise only rejects when the request never left the
    // browser (offline, blocked). That is the one failure we can see, so it's the only time we don't say thanks.
    setFailed(false)
    try {
      await fetch(endpoint, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString()
      })
      setSubmitted(true)
    } catch {
      setFailed(true)
    }
  }

  return (
    <div className="w-full max-w-sm">
      <p className="text-sm font-bold text-[#0F517B] dark:text-white">Stay in the loop</p>
      <p className="mt-1 text-sm text-[#0F1B2D]/70 dark:text-slate-300">
        Occasional, useful notes on school communication and what&apos;s new at School Journey.
      </p>
      {submitted ? (
        <p className="mt-3 text-sm font-semibold text-[#0F517B] dark:text-white">Thanks, we&apos;ll keep you posted!</p>
      ) : (
        // Plain label and input with fixed ids: the shared form helpers take their ids from useId,
        // which differed between server and browser here and logged a hydration warning on every page.
        // There is one footer per page, so fixed ids can't collide.
        <form className="mt-3 flex gap-2" onSubmit={form.handleSubmit(onSubmit)} noValidate>
          <div className="flex-1 space-y-2">
            <label htmlFor="newsletter-email" className="sr-only">
              Email address
            </label>
            <Input
              id="newsletter-email"
              type="email"
              placeholder="Your email address"
              className="h-10 rounded-full bg-white px-4 dark:bg-slate-900"
              aria-invalid={!!form.formState.errors.email}
              aria-describedby={form.formState.errors.email ? 'newsletter-email-error' : undefined}
              {...form.register('email')}
            />
            {form.formState.errors.email && (
              <p id="newsletter-email-error" className="text-sm font-medium text-destructive">
                {form.formState.errors.email.message}
              </p>
            )}
            {failed && (
              <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                That didn&apos;t go through. Check your connection and try again.
              </p>
            )}
          </div>
          <Button
            type="submit"
            disabled={form.formState.isSubmitting}
            className="h-10 rounded-full bg-[#0F517B] px-5 text-white transition-[background-color,color,box-shadow,transform] duration-300 ease-out hover:-translate-y-0.5 hover:bg-[#FFC933] hover:text-[#0F3D5F] hover:shadow-lg hover:shadow-[#0F3D5F]/25 motion-reduce:transition-colors motion-reduce:hover:translate-y-0"
          >
            Sign up
          </Button>
        </form>
      )}
    </div>
  )
}
